import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

const useDynamicContentLoop = ({ postId, postType, selectedSourceField = 'title', attributes }) => {
    // Fetch post data using useSelect
    const postData = useSelect(
        (select) => {
            return postId
                ? select(coreStore).getEntityRecord('postType', postType, postId)
                : null;
        },
        [postId, postType]
    );

    const getAuthorDetails = (post) => {
        return useSelect(
            (select) => {
                if (!post) {
                    return { name: '', nicName: '', fullName: '', lastName: '', bio: '', email: '', link: '', avatarUrl: '' };
                }
                const author = select(coreStore).getEntityRecord('root', 'user', post.author);
                return author
                    ? {
                          name: author.name || '',
                          nicName: author.nickname || '',
                          fullName: author.first_name || '',
                          lastName: author.last_name || '',
                          bio: author.description || '',
                          email: author.email || '',
                          link: author.link || '',
                          avatarUrl: author.avatar_urls ? author.avatar_urls[24] || '' : ''
                      }
                    : { name: '', nicName: '', fullName: '', lastName: '', bio: '', email: '', link: '', avatarUrl: '' };
            },
            [post]
        );
    };

    const formatDateTime = (rawDate, type = 'date') => {
        if (!rawDate) return '';
        const date = new Date(rawDate);
        return type === 'date'
            ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    const getFeaturedImageURL = (post) => {
        return useSelect(
            (select) => {
                if (!post || !post.featured_media) {
                    return ''; // Return empty string if no featured media
                }
                const media = select(coreStore).getEntityRecord('postType', 'attachment', post.featured_media);
                return media ? media.source_url : ''; // Return the URL if available
            },
            [post]
        );
    };

    const wrapWithLink = (content, { contentLinkUrl, contentLinkEnable, post, author, featuredImageURL, siteUrl }) => {
        
        if (!content || typeof content !== 'string') return ''; 
    
        // Avoid wrapping if the content already contains an anchor tag
        const parser = new DOMParser();
        const parsedContent = parser.parseFromString(content, 'text/html');
        if (parsedContent.querySelector('a')) {
            return content;
        }
    
        let dynamicUrl = '';
        if (contentLinkUrl === 'post_url' && post) {
            dynamicUrl = post.link;
        } else if (contentLinkUrl === 'site_url' && siteUrl) {
            dynamicUrl = siteUrl;
        } else if (contentLinkUrl === 'author_url' && author) {
            dynamicUrl = author.link;
        } else if (contentLinkUrl === 'archive_url' && post) {
            dynamicUrl = post.link.replace(/\/[^\/]*$/, '');
        } else if (contentLinkUrl === 'comments_url' && post) {
            dynamicUrl = post.link + '#comments';
        } else if (contentLinkUrl === 'featured_img_url') {
            dynamicUrl = featuredImageURL || '';
        }
    
        if (contentLinkEnable && dynamicUrl) {
            return `<a href="${dynamicUrl}" target="_blank" rel="noopener noreferrer">${content}</a>`;
        }
    
        return content;
    };
    

    const author = getAuthorDetails(postData);
    const featuredImageURL = getFeaturedImageURL(postData);

    // Safeguard for undefined attributes.content
    const safeContent = attributes?.content || '';

    // Determine content based on the selected source field
    const getContentToSet = () => {
        let contentToSet = '';
        if (postId) {
            switch (selectedSourceField) {
                case 'title':
                    contentToSet = postData?.title?.rendered || '';
                    break;
                case 'slug':
                    contentToSet = postData?.slug || '';
                    break;
                case 'post_date':
                    contentToSet = formatDateTime(postData.modified, 'date');
                    break;
                case 'post_time':
                    contentToSet = formatDateTime(postData.modified, 'time');
                    break;
                case 'excerpt':
                    contentToSet = postData?.excerpt?.rendered || '';
                    break;
                case 'content':
                    contentToSet = postData?.content?.rendered || '';
                    break;
                case 'post_id':
                    contentToSet = postId.toString() || '';
                    break;
                case 'author_name':
                    contentToSet = author.name;
                    break;
                case 'author_bio':
                    contentToSet = author.bio;
                    break;
                case 'author_email':
                    contentToSet = author.email;
                    break;
                case 'author_nic_name':
                    contentToSet = author.nicName;
                    break;
                case 'author_first_name':
                    contentToSet = author.firstName;
                    break;
                case 'author_last_name':
                    contentToSet = author.lastName;
                    break;
                case 'author_image':
                    contentToSet = author.avatarUrl ? `<figure class="author-avtar"><img src="${author.avatarUrl}" alt="author avtar" /></figure>` : '';
                    break;
                case 'post_image':
                    contentToSet = featuredImageURL ? `<figure class="wp-block-post-featured-image"><img src="${featuredImageURL}" alt="Featured Image" /></figure>` : '';
                    break;
                case 'comment_count':
                    contentToSet = postData.comment_count;
                    break;
                case 'none':
                    contentToSet = safeContent; // Safeguard for attributes.content
                    break;
            }
        }

        return contentToSet;
    };

    // Return the content fetched
    const content = getContentToSet();
    const { contentLinkEnable, contentLinkUrl } = attributes;
    return wrapWithLink(content, {
        contentLinkUrl,
        contentLinkEnable,
        post: postData,
        author,
        featuredImageURL
    });
};
export default useDynamicContentLoop;
